<template>
    <Modal close-esc-event="keydown" :close-button="false" modal-class="m-auto border-0" mask-class="grid">
        <template #toggle="scope">
            <span :ref="() => captureOpenFn(scope.open)" class="hidden"></span>
            <slot :open="scope.open" :close="scope.close" />
        </template>

        <template #header="scope">
            <CloseModalButton @close="scope.close"></CloseModalButton>
        </template>

        <template #body="scope">
            <div class="bg-white py-6 px-8 rounded-xl w-[400px]">
                <h2 class="text-xl font-semibold mb-6">{{ $t('views.reports.reportsPage.approveDifferenceModal.title') }}</h2>

                <CustomTextArea
                    v-model:value="comment"
                    :label="$t('views.reports.reportsPage.approveDifferenceModal.reasonLabel')"
                    :required="false"
                    :show-required-indication="false"
                    :maxlength="maxLength"
                    :details="commentError"
                    :error-state="isAtMaxLength"
                    @submit="onSave(scope.close)"
                ></CustomTextArea>

                <div class="mt-4 flex items-center gap-2 bg-primary-300 bg-opacity-20 border border-primary-300 border-opacity-40 text-primary-800 text-sm px-3 py-2.5 rounded-lg">
                    <Icon :src="IconSource.InfoIcon" class="w-4 h-4 flex-shrink-0"></Icon>
                    <span>{{ $t('views.reports.reportsPage.approveDifferenceModal.banner') }}</span>
                </div>

                <div class="flex space-x-4 mt-6">
                    <CustomButton class="flex-1" color-preset="secondary" @click="scope.close">
                        {{ $t('views.reports.reportsPage.approveDifferenceModal.cancelButton') }}
                    </CustomButton>
                    <CustomButton class="flex-1" @click="onSave(scope.close)">
                        {{ $t('views.reports.reportsPage.approveDifferenceModal.saveButton') }}
                    </CustomButton>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Modal } from '@makeabledk/vue-ui/components/modal';
import CustomButton from '@/components/ui/CustomButton.vue';
import CustomTextArea from '@/components/ui/CustomTextArea.vue';
import CloseModalButton from '@/components/common/CloseModalButton.vue';
import Icon from '@/components/icons/Icon.vue';
import { IconSource } from '@/types';

export default defineComponent({
    components: { Modal, CustomButton, CustomTextArea, CloseModalButton, Icon },
    props: {
        existingComment: {
            type: String,
            default: '',
        },
    },
    emits: ['save'],
    data() {
        return {
            IconSource,
            comment: this.existingComment,
            openFn: null as (() => void) | null,
            maxLength: 300,
        };
    },
    computed: {
        isAtMaxLength(): boolean {
            return this.comment?.length >= this.maxLength;
        },
        commentError(): { type: 'error' | 'hint'; text: string } | undefined {
            if (this.isAtMaxLength) {
                return { type: 'error', text: this.$t('views.reports.reportsPage.approveDifferenceModal.maxLengthError') };
            }
            return undefined;
        },
    },
    watch: {
        existingComment(newValue: string) {
            this.comment = newValue;
        },
    },
    methods: {
        captureOpenFn(openFn: () => void) {
            this.openFn = openFn;
        },
        onSave(closeModal: () => void) {
            this.$emit('save', this.comment);
            closeModal();
        },
        open() {
            this.comment = this.existingComment;
            this.openFn?.();
        },
    },
});
</script>
